import type { FriendGame, FriendLoungeResponse, FriendMember } from '@awwyeah/shared';

/**
 * The lounge's pure half (friend of the family): copy and small decisions, kept out of the
 * component so they're testable without React or a server.
 *
 * House law applies to every string here (`memory/no-demand-mechanics.md`): statements of
 * fact and a bit of cheek, never a count of how long somebody has left a move, never a
 * streak, never a nudge.
 */

/** The household member across the table in one of the friend's games. */
export function opponentIdOf(entry: FriendGame): string {
  return entry.game.playerIds.find((id) => id !== entry.seatId) ?? entry.game.playerIds[0];
}

export function memberName(members: readonly FriendMember[], id: string): string {
  return members.find((m) => m.id === id)?.name ?? 'Your host';
}

/** One plain fact per row. */
export function loungeStatusLine(entry: FriendGame, opponentName: string): string {
  if (entry.yourMove) return 'Your move';
  if (entry.finished) {
    const winner = entry.game.winnerId;
    if (winner === entry.seatId) return 'You won 🎉';
    if (winner) return `${opponentName} won`;
    return 'All done';
  }
  return `${opponentName}’s move`;
}

export function activeGameCount(games: readonly FriendGame[]): number {
  return games.filter((g) => !g.finished).length;
}

export function canStartAnother(lounge: FriendLoungeResponse): boolean {
  return activeGameCount(lounge.games) < lounge.maxActive && lounge.members.length > 0;
}

const QUIPS: ReadonlyArray<(name: string) => string> = [
  (name) => `Challenge sent. ${name}’s been warned.`,
  (name) => `Challenge sent. ${name} will see it next time they look.`,
  (name) => `Bold. ${name} has been informed.`,
  (name) => `It’s on. ${name} just doesn’t know it yet.`,
];

/** A little line for after a challenge goes out. `seed` keeps it stable per game. */
export function challengeQuip(name: string, seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  const pick = QUIPS[hash % QUIPS.length] ?? QUIPS[0];
  return pick ? pick(name) : `Challenge sent.`;
}

/** What to say when starting a game is refused. */
export function startErrorCopy(code: string, max: number): string {
  if (code === 'too_many') {
    return `That’s ${max} games on the go — finish one first. They’re not going anywhere.`;
  }
  if (code === 'not_your_friend') return 'You can only challenge people you’ve played with.';
  return 'Couldn’t start that one — try again in a bit.';
}

/** Newest first, as the server sends them — but kept here so the order is pinned by a test. */
export function sortLoungeGames(games: readonly FriendGame[]): FriendGame[] {
  return [...games].sort((a, b) => b.game.createdAt.localeCompare(a.game.createdAt));
}
